import {
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import { Paginator } from "primereact/paginator";
import { Link } from "react-router-dom";
import OperationalDataTableSkeleton from "../operational/OperationalDataTableSkeleton";
import FormSectionLoadingSkeleton from "../operational/FormSectionLoadingSkeleton";

const actionClasses = {
  primary:
    "border-[#2563EB] bg-[#2563EB] text-white hover:border-[#1D4ED8] hover:bg-[#1D4ED8] focus-visible:outline-[#2563EB]",
  green:
    "border-[#0D9488] bg-[#0D9488] text-white hover:border-[#0F766E] hover:bg-[#0F766E] focus-visible:outline-[#0D9488]",
  gray: "border-[#E2E8F0] bg-white text-[#0F172A] hover:border-[#94A3B8] hover:bg-[#F6F8FB] focus-visible:outline-[#64748B]",
};

function getActionIcon(action, index) {
  const label = action.label?.toLowerCase() ?? "";

  if (action.icon === "upload" || label.includes("upload")) {
    return <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />;
  }

  if (action.icon === "import" || label.includes("import")) {
    return <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />;
  }

  if (index === 0 || action.label?.toLowerCase().includes("add")) {
    return <PlusIcon className="h-4 w-4" aria-hidden="true" />;
  }

  return null;
}

export function SetupPage({
  title,
  eyebrow = "Master Data",
  description,
  searchPlaceholder = "Search",
  searchRef,
  onSearch,
  actions = [],
  loading,
  totalRecords = 0,
  first,
  rows,
  onPageChange,
  tableColumns,
  headerExtra,
  toolbarExtra,
  children,
}) {
  return (
    <div className="min-h-screen bg-[#F6F8FB] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-5">
        <section className="rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="border-b border-[#E2E8F0] px-5 py-5 sm:px-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-3xl">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#64748B]">
                  QA Trace / {eyebrow}
                </p>
                <h1 className="mt-1 text-[1.65rem] font-semibold tracking-tight text-[#0F172A] sm:text-3xl">
                  {title}
                </h1>
                {description ? (
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-[#64748B]">
                    {description}
                  </p>
                ) : null}
              </div>

              <div className="inline-flex w-fit items-center gap-2 rounded-xl border border-[#E2E8F0] bg-[#F6F8FB] px-3.5 py-2 text-sm font-medium text-[#0F172A]">
                <span className="h-2 w-2 rounded-full bg-[#0D9488]" />
                {totalRecords || 0} records
              </div>
            </div>
            {headerExtra ? <div className="mt-4">{headerExtra}</div> : null}
          </div>

          <div className="grid gap-3 px-5 py-4 sm:px-6 lg:grid-cols-[minmax(280px,420px)_1fr] lg:items-center">
            <div className="flex h-11 overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm focus-within:border-[#2563EB] focus-within:ring-2 focus-within:ring-[#2563EB]/10">
              <span className="inline-flex items-center pl-3 text-[#64748B]/70">
                <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <input
                type="search"
                className="h-full w-full border-0 bg-transparent px-3 text-sm text-[#0F172A] placeholder:text-[#64748B]/70 focus:outline-none focus:ring-0"
                placeholder={searchPlaceholder}
                ref={searchRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSearch?.();
                }}
              />
              <button
                type="button"
                onClick={() => onSearch?.()}
                className="border-l border-[#E2E8F0] px-4 text-sm font-semibold text-[#0F172A] transition hover:bg-[#F6F8FB] hover:text-[#2563EB]"
              >
                Search
              </button>
            </div>

            {actions.length > 0 || toolbarExtra ? (
              <div className="flex flex-wrap gap-2 lg:justify-end">
                {actions.map((action, index) => {
                  const ButtonOrLink = action.href ? Link : "button";
                  const classes =
                    actionClasses[action.variant || "primary"] ||
                    actionClasses.primary;
                  const icon = getActionIcon(action, index);

                  return (
                    <ButtonOrLink
                      key={`${action.label}-${index}`}
                      type={action.href ? undefined : "button"}
                      to={action.href}
                      target={action.target}
                      rel={action.rel}
                      onClick={action.onClick}
                      className={`inline-flex h-10 items-center justify-center gap-2 rounded-xl border px-3.5 text-sm font-semibold shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${classes}`}
                    >
                      {icon}
                      {action.label}
                    </ButtonOrLink>
                  );
                })}
                {toolbarExtra}
              </div>
            ) : null}
          </div>
        </section>

        <section className="overflow-hidden rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          {loading ? (
            tableColumns?.length ? (
              <OperationalDataTableSkeleton
                columns={[...tableColumns, "Actions"]}
                rows={8}
              />
            ) : (
              <div className="p-5">
                <FormSectionLoadingSkeleton rows={4} />
              </div>
            )
          ) : (
            <>{children}</>
          )}
        </section>

        {onPageChange ? (
          <div className="flex justify-end px-1">
            <Paginator
              first={first}
              rows={rows}
              totalRecords={totalRecords}
              onPageChange={onPageChange}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function SetupTable({ columns = [], children, emptyText, colSpan }) {
  const hasRows = Array.isArray(children)
    ? children.length > 0
    : Boolean(children);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                key={column}
                scope="col"
                className={`border-b border-[#E2E8F0] bg-[#EFF6FF] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748B] ${index === 0 ? "sm:pl-5" : ""}`}
              >
                {column}
              </th>
            ))}
            <th
              scope="col"
              className="border-b border-[#E2E8F0] bg-[#EFF6FF] px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748B] sm:pr-5"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {hasRows ? (
            children
          ) : (
            <tr>
              <td
                className="px-5 py-14 text-center text-sm text-[#64748B]"
                colSpan={colSpan || columns.length + 1}
              >
                {emptyText || "No records found."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function SetupTd({ children, muted = false, className = "" }) {
  return (
    <td
      className={`border-b border-[#E2E8F0]/70 px-4 py-3.5 align-middle text-sm ${muted ? "text-[#64748B]" : "font-medium text-[#0F172A]"} first:sm:pl-5 ${className}`.trim()}
    >
      {children || <span className="text-[#64748B]/70">—</span>}
    </td>
  );
}

export function SetupActions({ children }) {
  return (
    <td className="whitespace-nowrap border-b border-[#E2E8F0]/70 px-4 py-3.5 text-right text-sm font-medium sm:pr-5">
      <div className="inline-flex flex-wrap justify-end gap-2">{children}</div>
    </td>
  );
}
